import { NextPage } from 'next'
import Link from 'next/link'
import { useTranslation } from 'next-i18next'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCircleCheck } from '@fortawesome/free-regular-svg-icons'
import { faArrowUpRightFromSquare } from '@fortawesome/free-solid-svg-icons'
import { HtmlPageMeta } from '~/components/HtmlPageMeta/HtmlPageMeta'
import { HtmlPageMetaResourceService } from '~/components/HtmlPageMeta/HtmlPageMetaResourceService'
import {
  GetOrganizerResponseTranslator
} from '~/modules/Organizers/Application/GetOrganizer/GetOrganizerResponseTranslator'
import ApiTrophyResponseTranslator from '~/modules/Trophies/Application/ApiTrophyResponseTranslator'
import { ComponentTrophyDtoTransformer } from '~/modules/Products/Infrastructure/ComponentTrophyDtoTransformer'
import { OrganizerPageViewModel } from '~/modules/PresentationAdapters/OrganizerPageAdapter'
import { ComponentTrophyDto } from '~/modules/Products/Infrastructure/Dtos/ComponentTrophyDto'
import styles from './MarketOrganizerPage.module.scss'
import { Faq } from '~/components/Faq/Faq'

export interface MarketOrganizerPageProps {
  viewModel: OrganizerPageViewModel
}

export const MarketOrganizerPage: NextPage<MarketOrganizerPageProps> = ({ viewModel }) => {
  const { t } = useTranslation('organizer')
  const { t: tHeader } = useTranslation('organizer_header')
  const { organizer, trophyAmount } = GetOrganizerResponseTranslator
    .domainFromResponse(viewModel.organizerResponse)
  const htmlPageMetaUrlProps = (
    new HtmlPageMetaResourceService(organizer.name, t('organizer_page_description'))
  ).getProperties()
  const htmlPageMetaProps = { ...viewModel.htmlPageMetaContextProps, ...htmlPageMetaUrlProps }

  const featuredTrophy = viewModel.featuredTrophy !== null
    ? ApiTrophyResponseTranslator.domainFromResponse(viewModel.featuredTrophy)
    : null

  const latestTrophies = viewModel.trophies.map((trophy) => {
    return ComponentTrophyDtoTransformer.fromApplication(trophy)
  })

  const showcaseCompetition = featuredTrophy?.findAttribute('Competition')?.value || featuredTrophy?.name || ''
  const showcaseAward = featuredTrophy?.findAttribute('Award Type')?.value || ''
  const showcaseOwner = featuredTrophy?.findAttribute('Full Name')?.value || ''
  const organizerPlace = organizer.place?.toUpperCase() || '-'

  return (
    <>
      <HtmlPageMeta { ...htmlPageMetaProps } />

      <div className={ styles.marketOrganizerPage }>
        <header className={ styles.marketOrganizerPage__hero }>
          <div
            className={ styles.marketOrganizerPage__heroBanner }
            style={ {
              backgroundImage: organizer.bannerUrl ? `url(${organizer.bannerUrl})` : undefined,
            } }
          />

          <div className={ styles.marketOrganizerPage__heroCard }>
            <div className={ styles.marketOrganizerPage__heroMain }>
              <div className={ styles.marketOrganizerPage__heroAvatar }>
                <object
                  data={ organizer.avatarUrl ?? organizer.logoUrl ?? undefined }
                  className={ styles.marketOrganizerPage__heroAvatarInner }
                >
                  <img
                    src="/img/defaults/organizer.png"
                    className={ styles.marketOrganizerPage__heroAvatarInner }
                    alt={ tHeader('header_avatar_image') }
                    width={ 144 }
                    height={ 144 }
                  />
                </object>
              </div>

              <div className={ styles.marketOrganizerPage__heroIdentity }>
                <p className={ styles.marketOrganizerPage__heroEyebrow }>
                  { tHeader('organizer_title') }
                </p>

                <div className={ styles.marketOrganizerPage__heroTitleRow }>
                  <h1 className={ styles.marketOrganizerPage__heroTitle }>
                    { organizer.name }
                  </h1>

                  <FontAwesomeIcon
                    icon={ faCircleCheck }
                    className={ styles.marketOrganizerPage__heroCheck }
                  />
                </div>
              </div>
            </div>

            <div className={ styles.marketOrganizerPage__heroStats }>
              <div className={ `${styles.marketOrganizerPage__heroStat} ${styles.marketOrganizerPage__heroStat_top}` }>
                <span className={ styles.marketOrganizerPage__heroStatLabel }>
                  { t('organizer_page_region_label', { defaultValue: 'REGION' }) }
                </span>
                <strong className={ styles.marketOrganizerPage__heroStatValue }>
                  { organizerPlace }
                </strong>
              </div>

              <div
                className={ `${styles.marketOrganizerPage__heroStat} ${styles.marketOrganizerPage__heroStat_bottom}` }
              >
                <span className={ styles.marketOrganizerPage__heroStatLabel }>
                  ITEMS
                </span>
                <strong className={ styles.marketOrganizerPage__heroStatValue }>
                  { trophyAmount }
                </strong>
              </div>
            </div>
          </div>
        </header>

        <main className={ styles.marketOrganizerPage__content }>
          { featuredTrophy && (
            <section className={ styles.marketOrganizerPage__section }>
              <div className={ styles.marketOrganizerPage__sectionHeader }>
                <h2 className={ styles.marketOrganizerPage__sectionTitle }>
                  { t('organizer_page_showcase', { defaultValue: 'Showcase' }) }
                </h2>
              </div>

              <Link
                href={ `/trophies/${featuredTrophy.id}` }
                className={ styles.marketOrganizerPage__showcase }
                passHref
              >
                <div className={ styles.marketOrganizerPage__showcaseMedia }>
                  <img
                    src={ featuredTrophy.rawImageUrl }
                    alt={ featuredTrophy.description }
                    className={ styles.marketOrganizerPage__showcaseImage }
                    width={ 720 }
                    height={ 720 }
                    loading="lazy"
                  />
                </div>

                <div className={ styles.marketOrganizerPage__showcaseBody }>
                  <div className={ styles.marketOrganizerPage__showcaseHeadline }>
                    <div className={ styles.marketOrganizerPage__showcaseHeadlineCopy }>
                      <h3 className={ styles.marketOrganizerPage__showcaseCompetition }>
                        { showcaseCompetition }
                      </h3>

                      { showcaseAward && (
                        <p className={ styles.marketOrganizerPage__showcaseAward }>
                          { showcaseAward }
                        </p>
                      ) }
                    </div>

                    <FontAwesomeIcon
                      icon={ faArrowUpRightFromSquare }
                      className={ styles.marketOrganizerPage__showcaseArrow }
                    />
                  </div>

                  { showcaseOwner && (
                    <div className={ styles.marketOrganizerPage__showcaseOwnerRow }>
                      <p className={ styles.marketOrganizerPage__showcaseOwner }>
                        { showcaseOwner }
                      </p>
                    </div>
                  ) }

                  <div className={ styles.marketOrganizerPage__showcaseDescription }>
                    <p className={ styles.marketOrganizerPage__showcaseDescriptionLabel }>
                      { t('organizer_page_description_label', { defaultValue: 'Description' }) }
                    </p>

                    <p className={ styles.marketOrganizerPage__showcaseDescriptionText }>
                      { featuredTrophy.description }
                    </p>
                  </div>
                </div>
              </Link>
            </section>
          ) }

          { latestTrophies.length > 0 && (
            <section className={ styles.marketOrganizerPage__section }>
              <div className={ styles.marketOrganizerPage__sectionHeader }>
                <div className={ styles.marketOrganizerPage__sectionHeadingGroup }>
                  <h2 className={ styles.marketOrganizerPage__sectionTitle }>
                    { t('organizer_page_overview', { defaultValue: 'Overview' }) }
                  </h2>

                  <span className={ styles.marketOrganizerPage__sectionMeta }>
                    { trophyAmount }
                    { ' ' }
                    { t('organizer_page_items_label', { defaultValue: 'ITEMS' }) }
                  </span>
                </div>

                <Link
                  href={ `/trophies?organizerId=${organizer.id}` }
                  className={ styles.marketOrganizerPage__sectionLink }
                  passHref
                >
                  { t('organizer_page_more', { defaultValue: 'MORE' }) }
                  <FontAwesomeIcon icon={ faArrowUpRightFromSquare } />
                </Link>
              </div>

              <div className={ styles.marketOrganizerPage__overviewGrid }>
                { latestTrophies.slice(0, 4).map((trophy) => (
                  <OrganizerOverviewCard
                    key={ trophy.id }
                    trophy={ trophy }
                  />
                )) }
              </div>
            </section>
          ) }

          <section className={ styles.marketOrganizerPage__section }>
            <Faq appearance="museumHome" />
          </section>
        </main>
      </div>
    </>
  )
}

interface OrganizerOverviewCardProps {
  trophy: ComponentTrophyDto
}

const OrganizerOverviewCard = ({ trophy }: OrganizerOverviewCardProps) => {
  return (
    <Link
      href={ `/trophies/${trophy.id}` }
      className={ styles.marketOrganizerPage__overviewCard }
      passHref
    >
      <div className={ styles.marketOrganizerPage__overviewCardMedia }>
        <img
          src={ trophy.imageUrl }
          alt={ trophy.description }
          className={ styles.marketOrganizerPage__overviewCardImage }
          width={ 640 }
          height={ 640 }
          loading="lazy"
        />
      </div>

      <div className={ styles.marketOrganizerPage__overviewCardBody }>
        <h3 className={ styles.marketOrganizerPage__overviewCardTitle }>
          { trophy.content.title }
        </h3>

        <div className={ styles.marketOrganizerPage__overviewCardMeta }>
          <p className={ styles.marketOrganizerPage__overviewCardCategory }>
            { trophy.content.header }
          </p>

          <p className={ styles.marketOrganizerPage__overviewCardOwner }>
            { trophy.content.subtitle }
          </p>
        </div>
      </div>
    </Link>
  )
}
